import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Paper from '@mui/material/Paper';
import TableHeading from '../../components/Table/TableHeading';
import Row from '../../components/Table/Row';
import FiltersSidebar from './FiltersSidebar';
import { pagesText } from '../../constants/pagesText';
import LoadingRows from '../../components/Table/LoadingRows';
import useQueryParams from '../../hooks/useQueryParams';
import useDebounceHandler from '../../hooks/useDebounceHandler';
import NoDataCell from '../../components/NoDataCell';
import { defaultAppsFilters } from '../../components/Filters/constants';
import useTableSort from '../../hooks/useTableSort';
import { checkHasFilters } from '../../utils/table.util';
import useResetSearchParams from '../../hooks/useResetSearchParams';
import { PORTAL_ROUTE } from '../../constants/routes';
import TableContainer from '../../components/Table/TableContainer';
import useFetchApps from '../../queries/useFetchApps';
import FilterCount from '../../components/Filters/FilterCount';
import { defaultSearchParams, reversedSortMap, sortingMethods } from 'src/constants/table';
import { Box, TableCell, Chip } from '@mui/material';
import TableSearch from 'src/components/elements/TableSearch';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import ApplicationsService from '../../services/applications.service';
import { APPLICATIONS_QUERY_KEY, APPLICATION_VERSIONS_QUERY_KEY } from '../../constants/query';
import StatusUpdateModal from './StatusUpdateModal';
import GenericTableCell from '../../components/GenericTableCell';
import { isDateString } from '../../utils/date.util';
import { formatDate } from '../../utils/formatTime';
import TableNameCell from '../../components/Table/TableNameCell';
import { getCustomerEnvironmentApplicationStatusValue } from 'src/utils/enums/customerEnvironmentApplicationStatus';

export const SOURCE_BADGE_META = {
  'wdac import': {
    label: 'WDAC Import',
    color: '#8EB4FF',
    borderColor: 'rgba(142, 180, 255, 0.4)',
    backgroundColor: 'rgba(48, 91, 173, 0.25)',
  },
  'installation import': {
    label: 'Installation Import',
    color: '#9EE6C4',
    borderColor: 'rgba(158, 230, 196, 0.4)',
    backgroundColor: 'rgba(62, 118, 105, 0.25)',
  },
};

const Table = ({ headings, columnsOrder }) => {
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [modal, setModal] = useState({
    open: false,
    id: null,
    currentStatus: null,
    currentNote: null,
    currentDate: null,
  });
  const [statusOverrides, setStatusOverrides] = useState({});
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  useResetSearchParams();

  const { data, lastElementRef, isLoading, isFetching, applicationsCount } = useFetchApps({});
  const {
    queryObject: { filters = {}, searchValue: initialSearchValue },
    onApplySearchParams,
    changeFieldInURL,
  } = useQueryParams();
  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const { onSort, onSortTable, onResetSortField, getListItemSortMethod, getCellSortMethod } =
    useTableSort({
      filters,
      onApplySearchParams,
      searchValue,
      defaultFilters: defaultAppsFilters,
    });

  const debounceQueryHandler = useDebounceHandler((key, value) => {
    changeFieldInURL([{ key, value }]);
  }, 500);

  const onChangeSearchValue = useCallback(
    event => {
      setSearchValue(event.target.value);
      debounceQueryHandler('search', event.target.value.trim());
    },
    [debounceQueryHandler, setSearchValue],
  );

  const hasFilters = useMemo(() => checkHasFilters(filters), [filters]);

  const onApplyFilters = useCallback(
    filters => {
      onApplySearchParams({ filters, searchValue });
    },
    [onApplySearchParams, searchValue],
  );

  const onOpenFilters = useCallback(() => {
    setIsOpenFilters(true);
  }, [setIsOpenFilters]);

  const onCloseFilters = useCallback(() => {
    setIsOpenFilters(false);
  }, [setIsOpenFilters]);

  const onResetSearch = useCallback(() => {
    setSearchValue('');
    changeFieldInURL([defaultSearchParams]);
  }, [setSearchValue, changeFieldInURL]);

  const onCellSort = useCallback(
    ({ field, sortingCellField, sortMethod }) => {
      const reversedSortOrder =
        sortMethod === sortingMethods.initial ? sortingMethods.asc : reversedSortMap[sortMethod];
      onSort(field, reversedSortOrder, sortingCellField);
    },
    [onSort],
  );

  const navigateToApp = useCallback(id => {
    navigate(`${PORTAL_ROUTE}/apps/${id}/general`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredCount = useMemo(() => {
    return !isLoading && (hasFilters || searchValue) ? applicationsCount : null;
  }, [applicationsCount, hasFilters, isLoading, searchValue]);

  const handleOpenStatusModal = useCallback(
    ({ applicationId, status, notes, date }) => {
      if (!applicationId) {
        return;
      }

      setModal({
        open: true,
        id: applicationId,
        currentStatus: getCustomerEnvironmentApplicationStatusValue(status),
        currentNote: notes || '',
        currentDate: date || null,
      });
    },
    [setModal],
  );

  const updateApplicationsCache = useCallback(
    ({ applicationId, status, notes, date }) => {
      if (!applicationId) {
        return;
      }

      const updatedDate = date || new Date().toISOString();
      const nextStatus = getCustomerEnvironmentApplicationStatusValue(status);
      const targetId = String(applicationId);
      const updater = cachedData => {
        if (!cachedData?.pages?.length) {
          return cachedData;
        }

        const updatedPages = cachedData.pages.map(page => {
          if (!page?.applications?.length) {
            return page;
          }

          const updatedApplications = page.applications.map(app => {
            const appIdCandidate =
              app.customerEnvironmentApplicationId ?? app.id ?? app.applicationId;
            if (!appIdCandidate || String(appIdCandidate) !== targetId) {
              return app;
            }

            return {
              ...app,
              status: nextStatus,
              notes,
              statusUpdatedOn: updatedDate,
            };
          });

          return {
            ...page,
            applications: updatedApplications,
          };
        });

        return {
          ...cachedData,
          pages: updatedPages,
        };
      };

      setStatusOverrides(prev => ({
        ...prev,
        [targetId]: {
          status: nextStatus,
          notes: notes || '',
          date: updatedDate,
        },
      }));

      queryClient
        .getQueryCache()
        .findAll({ queryKey: [APPLICATIONS_QUERY_KEY] })
        .forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, updater);
        });

      queryClient
        .getQueryCache()
        .findAll({ queryKey: [APPLICATION_VERSIONS_QUERY_KEY] })
        .forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, cachedData => {
            if (!cachedData?.pages?.length) {
              return cachedData;
            }

            const updatedPages = cachedData.pages.map(page => {
              if (!page?.versions?.length) {
                return page;
              }

              const updatedVersions = page.versions.map(version => {
                const versionAppId =
                  version.customerEnvironmentApplicationId ?? version.applicationId ?? version.id;
                if (!versionAppId || String(versionAppId) !== targetId) {
                  return version;
                }

                return {
                  ...version,
                  status: nextStatus,
                  statusNotes: notes,
                  statusUpdatedOn: updatedDate,
                  customerEnvironmentApplicationId: versionAppId,
                };
              });

              return {
                ...page,
                versions: updatedVersions,
              };
            });

            return {
              ...cachedData,
              pages: updatedPages,
            };
          });
        });
    },
    [queryClient],
  );

  const { mutate: updateStatus, isLoading: statusLoading } = useMutation(
    ApplicationsService.updateApplicationStatus,
    {
      onError: () => {
        enqueueSnackbar(pagesText.somethingWentWrong, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      },
      onSuccess: (_, variables) => {
        updateApplicationsCache({
          applicationId: variables.customerEnvironmentApplicationId,
          status: variables.status,
          notes: variables.notes,
          date: new Date().toISOString(),
        });
        enqueueSnackbar('Status has been updated successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        setModal(prev => ({ ...prev, open: false }));
      },
    },
  );

  const handleRowExpandChange = useCallback((rowId, isExpanded) => {
    setExpandedRowIds(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  }, []);

  const dynamicHeadings = useMemo(() => {
    const hasExpandedRows = expandedRowIds.size > 0;
    if (!hasExpandedRows) {
      return headings;
    }

    const statusHeader = {
      title: 'Status',
      name: 'status',
      hasSorting: false,
      sortMethod: sortingMethods.initial,
      width: '10%',
    };

    const headingsCopy = [...headings];
    // Find the index of source header and insert status after it
    const sourceIndex = headingsCopy.findIndex(heading => heading?.name === 'source');
    if (sourceIndex !== -1) {
      // Insert status after source, before the empty column at the end
      headingsCopy.splice(sourceIndex + 1, 0, statusHeader);
    } else {
      // If source not found, insert before last element (empty column) as fallback
      headingsCopy.splice(headingsCopy.length - 1, 0, statusHeader);
    }
    return headingsCopy;
  }, [headings, expandedRowIds]);

  const dynamicColumnsOrder = useMemo(() => {
    const hasExpandedRows = expandedRowIds.size > 0;
    if (!hasExpandedRows) {
      return columnsOrder;
    }

    const columnsCopy = [...columnsOrder];
    const hasStatus = columnsCopy.some(col => col?.name === 'status');
    if (!hasStatus) {
      // Find the index of source column and insert status after it
      const sourceIndex = columnsCopy.findIndex(col => col?.name === 'source');
      if (sourceIndex !== -1) {
        columnsCopy.splice(sourceIndex + 1, 0, { name: 'status' });
      } else {
        // If source not found, add status at the end as fallback
        columnsCopy.push({ name: 'status' });
      }
    }
    return columnsCopy;
  }, [columnsOrder, expandedRowIds]);

  const formatSourceValue = useCallback(source => {
    if (!source) return '';
    return source
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  const getSourceBadgeMeta = useCallback(
    source => {
      if (!source) {
        return {
          label: '-',
          color: '#DDDDDD',
          borderColor: 'rgba(221, 221, 221, 0.3)',
          backgroundColor: 'transparent',
        };
      }

      const normalized = source.toLowerCase();
      if (SOURCE_BADGE_META[normalized]) {
        return SOURCE_BADGE_META[normalized];
      }

      return {
        label: formatSourceValue(source),
        color: '#DDDDDD',
        borderColor: 'rgba(221, 221, 221, 0.3)',
        backgroundColor: 'transparent',
      };
    },
    [formatSourceValue],
  );

  const renderCustomCell = useCallback(
    (column, row) => {
      // shows empty cell in main table when row is expanded
      if (column?.name === 'status') {
        return <TableCell key="status" />;
      }

      if (column?.name === 'environmentApplicationName') {
        return (
          <TableNameCell
            key={row.environmentApplicationName}
            name={row.environmentApplicationName}
            imgSrc={row.icon}
            isRowActive={false}
            {...column?.props}
          />
        );
      }

      if (column?.name === 'source') {
        const badgeMeta = getSourceBadgeMeta(row.source);
        return (
          <TableCell key="source">
            <Chip
              size="small"
              label={badgeMeta.label}
              sx={{
                height: 22,
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'none',
                color: badgeMeta.color,
                borderColor: badgeMeta.borderColor,
                backgroundColor: badgeMeta.backgroundColor,
              }}
              variant="outlined"
            />
          </TableCell>
        );
      }

      return (
        <GenericTableCell
          key={column?.name || column}
          isNumber={column?.isNumber}
          value={
            column?.shouldHideValue
              ? ''
              : isDateString(row[column?.name || column])
                ? formatDate(row[column?.name || column])
                : row[column?.name || column]
          }
          styles={column?.styles || {}}
          {...column?.props}
        />
      );
    },
    [getSourceBadgeMeta],
  );

  return (
    <Box sx={{ position: 'relative' }} mt={4}>
      <FilterCount filteredCount={filteredCount} onOpenFilters={onOpenFilters} />
      <TableSearch
        onResetSearch={onResetSearch}
        searchValue={searchValue}
        onChangeSearchValue={onChangeSearchValue}
      />
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <TableContainer
          sx={{ maxHeight: 'calc(100vh - 274px)' }}
          square
          component={Paper}
          elevation={0}>
          <MuiTable stickyHeader aria-label="collapsible table">
            <TableHeading
              hasFilters={hasFilters}
              onOpenFilters={onOpenFilters}
              headings={dynamicHeadings}
              onSortTable={onSortTable}
              onCellSort={onCellSort}
              onResetSortField={onResetSortField}
              getListItemSortMethod={getListItemSortMethod}
              getCellSortMethod={getCellSortMethod}
              listCellStyles={{
                top: '0',
              }}
            />
            <TableBody>
              {isLoading ? (
                <LoadingRows columnCount={columnsOrder.length + 1} rowCount={5} />
              ) : (
                <>
                  {data.map((row, index) => {
                    const rowKey =
                      row.customerEnvironmentApplicationId ?? row.applicationId ?? row.id;
                    const applicationIdentifier = rowKey ?? row.id;
                    const override = applicationIdentifier
                      ? statusOverrides[String(applicationIdentifier)]
                      : undefined;
                    const rowStatus = getCustomerEnvironmentApplicationStatusValue(
                      override?.status ?? row.status,
                    );
                    const rowNotes = override?.notes ?? row.notes;
                    const rowStatusUpdatedOn = override?.date ?? row.statusUpdatedOn;
                    const rowId = row.id;

                    const expandedRowPropsValue = {
                      applicationStatus: rowStatus,
                      applicationNotes: rowNotes,
                      applicationStatusUpdatedOn: rowStatusUpdatedOn,
                      applicationId: applicationIdentifier,
                      onStatusClick: payload =>
                        handleOpenStatusModal({
                          applicationId: payload?.applicationId ?? applicationIdentifier,
                          status: payload?.status ?? rowStatus,
                          notes: payload?.notes ?? rowNotes,
                          date: payload?.date ?? rowStatusUpdatedOn,
                        }),
                    };

                    return (
                      <Row
                        ref={data.length - 1 === index ? lastElementRef : null}
                        key={rowId}
                        row={row}
                        columnData={dynamicColumnsOrder}
                        rowClickHandler={navigateToApp}
                        isClickable
                        source="apps"
                        renderCustomCell={renderCustomCell}
                        onExpandChange={handleRowExpandChange}
                        expandedRowProps={expandedRowPropsValue}
                      />
                    );
                  })}
                  {!data.length && <NoDataCell>{pagesText.noSearchResult}</NoDataCell>}
                  {isFetching && !isLoading && (
                    <LoadingRows columnCount={columnsOrder.length + 1} rowCount={5} />
                  )}
                </>
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
        <FiltersSidebar
          open={isOpenFilters}
          onClose={onCloseFilters}
          onApplyFilters={onApplyFilters}
          initialQueryFilters={filters}
          source="app"
        />
      </Paper>
      <StatusUpdateModal
        open={modal.open}
        onClose={() => setModal(prev => ({ ...prev, open: false }))}
        currentStatus={modal.currentStatus}
        currentNote={modal.currentNote}
        currentDate={modal.currentDate}
        onSave={(newStatus, note) => {
          if (!modal.id) return;
          updateStatus({
            customerEnvironmentApplicationId: modal.id,
            status: newStatus,
            notes: note,
          });
        }}
        loading={statusLoading}
      />
    </Box>
  );
};

export default Table;
